import React, { useEffect, useState } from "react";
import { Activity, User, Filter, FileSpreadsheet, FileDown, Loader2 } from "lucide-react";
import { api, fmtDateTime } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const ActivityLog = ({ role }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState([]);
  const [username, setUsername] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(null);
  const canSeeAll = role === "kumendan" || role === "manager";

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get("/activities/report", {
        params: { month, format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kinerja-${month}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Laporan kinerja ${format.toUpperCase()} diunduh`);
    } catch {
      toast.error("Gagal mengunduh laporan kinerja");
    } finally {
      setBusy(null);
    }
  };

  const load = async () => {
    const params = {};
    if (canSeeAll && username.trim()) params.username = username.trim().toLowerCase();
    const [a, s] = await Promise.all([
      api.get("/activities", { params }),
      api.get("/activities/summary"),
    ]);
    setItems(a.data);
    setSummary(s.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  return (
    <div className="space-y-6" data-testid="activity-log">
      <div className="glass rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-blue-400" />
            <h3 className="font-display text-xl font-semibold">
              Rekap Aktivitas per Akun
            </h3>
            <span className="text-[11px] text-slate-500">(tanpa login)</span>
          </div>
          {canSeeAll && (
            <div className="flex flex-wrap items-center gap-3" data-testid="activity-report-tools">
              <Input
                data-testid="report-month-input"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="h-10 w-auto bg-slate-900/70 border-white/10"
              />
              <Button
                data-testid="report-xlsx-btn"
                onClick={() => download("xlsx")}
                disabled={busy}
                className="h-10 rounded-xl bg-emerald-600/90 hover:bg-emerald-500 px-4 font-semibold"
              >
                {busy === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel</>}
              </Button>
              <Button
                data-testid="report-pdf-btn"
                onClick={() => download("pdf")}
                disabled={busy}
                className="h-10 rounded-xl bg-red-600/80 hover:bg-red-500 px-4 font-semibold"
              >
                {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-2" /> PDF</>}
              </Button>
            </div>
          )}
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="activity-summary">
          {summary.length === 0 && (
            <p className="text-sm text-slate-500">Belum ada aktivitas tercatat.</p>
          )}
          {summary.map((s) => (
            <div
              key={s.username}
              data-testid={`activity-summary-${s.username}`}
              className="rounded-xl border border-white/10 bg-slate-900/60 p-5 lift"
            >
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400">
                {s.role_label}
              </div>
              <div className="mt-1 font-display text-lg font-semibold text-slate-100">
                {s.username}
              </div>
              <div className="mt-3 font-display text-3xl font-light text-blue-300">
                {s.total}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                aktivitas · terakhir {fmtDateTime(s.last)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="p-5 md:p-6 border-b border-white/10 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-blue-400" />
            <h3 className="font-display text-xl font-semibold">
              Riwayat Aktivitas{" "}
              <span className="text-slate-500 text-sm font-normal">({items.length})</span>
            </h3>
          </div>
          {canSeeAll && (
            <div className="relative">
              <Filter className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="activity-user-filter"
                placeholder="Filter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-10 pl-9 w-56 bg-slate-900/70 border-white/10"
              />
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="activity-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-slate-400">
                <th className="px-5 py-4 font-semibold">Tanggal & Waktu</th>
                <th className="px-5 py-4 font-semibold">Akun</th>
                <th className="px-5 py-4 font-semibold">Aktivitas</th>
                <th className="px-5 py-4 font-semibold">Keterangan</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-slate-500">
                    Belum ada aktivitas.
                  </td>
                </tr>
              )}
              {items.map((a) => (
                <tr key={a.id} className="border-t border-white/5 hover:bg-blue-500/5 transition-colors duration-200">
                  <td className="px-5 py-3.5 text-slate-300 whitespace-nowrap">{fmtDateTime(a.at)}</td>
                  <td className="px-5 py-3.5">
                    <div className="text-slate-200">{a.username}</div>
                    <div className="text-[11px] text-slate-500">{a.role_label}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="rounded-md border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-200">
                      {a.action}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-400">{a.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
